import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useForm } from "../FormContext";

interface ImageUploaderProps {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  name,
  placeholder,
  className,
}) => {
  const { fields, setFieldValue } = useForm();
  const [preview, setPreview] = useState<string | null>(fields[name]?.value || null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          setFieldValue(name, base64String);
          setPreview(base64String);
        };
        reader.readAsDataURL(file);
      }
    },
    [name, setFieldValue],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const handleRemove = () => {
    setFieldValue(name, "");
    setPreview(null);
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ease-in-out cursor-pointer
        ${
          isDragActive
            ? "border-primary bg-blue-50 dark:bg-blue-900/20"
            : "border-gray-300 hover:border-primary dark:border-gray-600 dark:hover:border-primary"
        }
        ${
          preview ? "border-solid" : ""
        }`}
      >
        <input {...getInputProps()} />

        {preview ? (
          <div className="relative group">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-auto max-h-48 object-contain rounded-md"
            />
            <div
              className="absolute top-2 right-2 flex items-center justify-center w-8 h-8 bg-red-600 rounded-full cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation(); // Prevent dropzone click event
                handleRemove();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center">
            <svg
              className="w-12 h-12 mx-auto text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              {isDragActive
                ? "Drop the files here ..."
                : placeholder || "Drag & drop an image here, or click to select"}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              PNG, JPG, GIF up to 10MB
            </p>
          </div>
        )}
      </div>
    </div>
  );
}; 